import React, { useState } from 'react';
import { ArrowRight } from 'lucide-react';
import { PLATES, Plate } from '../../constants/plates';
import BreakdownSheet from './BreakdownSheet';

const MACRO_COLORS = { protein: '#FFE7CE', carbs: '#F9FFCC', fat: '#F1DFFF' };

const grams = (value: string) => parseFloat(value) || 0;

const scoreColor = (score: number) => {
    if (score >= 75) return '#02F3A2';
    if (score >= 55) return '#FCD903';
    return '#FF6A59';
};

/** Short label for the plate selector pills. */
const shortName = (plate: Plate) => plate.food_item.split(' with ')[0];

const AnalysisSection: React.FC = () => {
    const [selected, setSelected] = useState<Plate>(PLATES[0]);
    const [sheetOpen, setSheetOpen] = useState(false);

    // Macro bar widths are calorie-weighted: protein/carbs 4 kcal per g, fat 9.
    const p = grams(selected.protein) * 4;
    const c = grams(selected.carbohydrates) * 4;
    const f = grams(selected.fat) * 9;
    const total = p + c + f || 1;

    return (
        <section id="analyse" className="relative z-[1] scroll-mt-24 px-5 py-[clamp(64px,9vw,120px)]">
            <div className="mx-auto max-w-[1180px]">
                <div className="flex flex-wrap items-end justify-between gap-5">
                    <div className="max-w-[660px]">
                        <span className="text-[10px] font-medium uppercase tracking-[0.16em] text-success">
                            Real meals, real numbers
                        </span>
                        <h2 className="mt-3.5 text-[clamp(28px,3.8vw,48px)] font-extrabold leading-[1.04] tracking-[-0.035em] text-white [text-wrap:balance]">
                            Every plate, broken down
                        </h2>
                    </div>
                    <p className="max-w-[36ch] text-[15.5px] leading-relaxed text-dark-textSecondary">
                        Photographs run through Fork's analysis. Tap any plate for the full breakdown.
                    </p>
                </div>

                <div className="mt-[clamp(28px,4vw,44px)] grid items-start gap-[clamp(16px,2.6vw,28px)] lg:grid-cols-[minmax(300px,1.06fr)_minmax(300px,0.94fr)]">
                    {/* featured plate */}
                    <div className="relative overflow-hidden rounded-3xl bg-[#202428]">
                        <img
                            src={selected.image}
                            alt={selected.food_item}
                            loading="lazy"
                            className="aspect-[4/3.2] w-full object-cover transition-opacity duration-[260ms]"
                            style={{ objectPosition: selected.pos }}
                        />
                        <span
                            aria-hidden="true"
                            className="pointer-events-none absolute inset-0 bg-[linear-gradient(180deg,rgba(15,19,22,0)_42%,rgba(15,19,22,0.92)_100%)]"
                        />
                        <span className="absolute left-4 top-4 rounded-full border border-dark-gunmetal bg-[rgba(15,19,22,0.72)] px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.1em] text-dark-textSecondary backdrop-blur-xl">
                            {selected.origin}
                        </span>
                        <div className="absolute inset-x-5 bottom-[18px]">
                            <p className="text-[clamp(20px,2.4vw,26px)] font-bold leading-[1.2] tracking-[-0.02em] text-white">
                                {selected.food_item}
                            </p>
                            <p className="mt-1.5 text-[11px] font-medium uppercase tracking-[0.1em] text-dark-textSecondary">
                                {selected.weight} · {selected.processing_label} · {selected.meal_type}
                            </p>
                        </div>
                    </div>

                    {/* analysis */}
                    <div className="min-w-0 pt-1.5">
                        <div className="flex items-end gap-[clamp(20px,3vw,36px)]">
                            <div>
                                <p className="mb-1.5 text-[10px] font-medium uppercase tracking-[0.14em] text-dark-textSubtle">
                                    Nutrition score
                                </p>
                                <p className="flex items-baseline gap-1">
                                    <span
                                        className="text-[clamp(40px,5vw,58px)] font-extrabold leading-[0.9] tracking-[-0.03em]"
                                        style={{ color: scoreColor(selected.score) }}
                                    >
                                        {selected.score}
                                    </span>
                                    <span className="text-sm font-semibold text-dark-textSubtle">/100</span>
                                </p>
                            </div>
                            <div>
                                <p className="mb-1.5 text-[10px] font-medium uppercase tracking-[0.14em] text-dark-textSubtle">
                                    Calories
                                </p>
                                <p className="flex items-baseline gap-1">
                                    <span className="text-[clamp(40px,5vw,58px)] font-extrabold leading-[0.9] tracking-[-0.03em] text-white">
                                        {grams(selected.calories)}
                                    </span>
                                    <span className="text-sm font-semibold text-dark-textSubtle">kcal</span>
                                </p>
                            </div>
                        </div>

                        {/* macro split */}
                        <span className="mt-7 flex h-2 gap-[3px] overflow-hidden rounded-full">
                            <span style={{ width: `${(p / total) * 100}%`, background: MACRO_COLORS.protein }} />
                            <span style={{ width: `${(c / total) * 100}%`, background: MACRO_COLORS.carbs }} />
                            <span style={{ width: `${(f / total) * 100}%`, background: MACRO_COLORS.fat }} />
                        </span>
                        <div className="mt-3 flex flex-wrap gap-[18px]">
                            {[
                                { label: 'Protein', value: selected.protein, color: MACRO_COLORS.protein },
                                { label: 'Carbs', value: selected.carbohydrates, color: MACRO_COLORS.carbs },
                                { label: 'Fat', value: selected.fat, color: MACRO_COLORS.fat },
                            ].map((m) => (
                                <span
                                    key={m.label}
                                    className="inline-flex items-center gap-[7px] text-[13px] text-dark-textSecondary"
                                >
                                    <span
                                        className="h-2 w-2 rounded-sm"
                                        style={{ background: m.color }}
                                    />
                                    {m.label} <strong className="font-semibold text-white">{m.value}</strong>
                                </span>
                            ))}
                        </div>

                        {/* secondary stats */}
                        <div className="mt-7 grid grid-cols-2 gap-3.5 border-t border-dark-gunmetal pt-6 sm:grid-cols-4">
                            {[
                                { label: 'Fiber', value: selected.fiber },
                                { label: 'Sugar', value: selected.natural_sugar },
                                { label: 'Sodium', value: selected.sodium },
                                { label: 'Density', value: `${selected.micronutrient_score}/10` },
                            ].map((s) => (
                                <div key={s.label}>
                                    <p className="mb-[5px] text-[10px] uppercase tracking-[0.1em] text-dark-textSubtle">
                                        {s.label}
                                    </p>
                                    <p className="text-base font-semibold text-white">{s.value}</p>
                                </div>
                            ))}
                        </div>

                        {/* highlights */}
                        <div className="mt-6 grid gap-2.5 border-t border-dark-gunmetal pt-6">
                            <p className="text-[15px] leading-[1.55] text-dark-textSecondary">
                                <span className="text-success">+</span> {selected.positives[0]}
                            </p>
                            <p className="text-[15px] leading-[1.55] text-dark-textSecondary">
                                <span className="text-[#DDDD61]">!</span> {selected.considerations[0]}
                            </p>
                        </div>

                        <button
                            onClick={() => setSheetOpen(true)}
                            className="mt-[22px] inline-flex items-center gap-2 text-[13px] font-semibold tracking-[0.02em] text-[#0393E7] transition-colors hover:text-success"
                        >
                            Full breakdown
                            <ArrowRight className="h-3.5 w-3.5" />
                        </button>
                    </div>
                </div>

                {/* plate selector */}
                <div className="mt-[clamp(24px,3vw,32px)] flex flex-wrap gap-2.5">
                    {PLATES.map((plate) => {
                        const active = plate.food_item === selected.food_item;
                        return (
                            <button
                                key={plate.food_item}
                                onClick={() => setSelected(plate)}
                                aria-label={plate.food_item}
                                aria-pressed={active}
                                className={`flex h-11 items-center gap-2.5 rounded-full pl-1.5 pr-4 transition-colors duration-[280ms] ${
                                    active ? 'bg-[#2E373C]' : 'bg-transparent hover:bg-[#262B2F]'
                                }`}
                            >
                                <span
                                    className="h-8 w-8 shrink-0 rounded-full bg-cover bg-center"
                                    style={{ backgroundImage: `url(${plate.image})` }}
                                />
                                <span
                                    className={`whitespace-nowrap text-[13px] font-semibold ${
                                        active ? 'text-white' : 'text-dark-textSecondary'
                                    }`}
                                >
                                    {shortName(plate)}
                                </span>
                            </button>
                        );
                    })}
                </div>
            </div>

            <BreakdownSheet
                plate={sheetOpen ? selected : null}
                onClose={() => setSheetOpen(false)}
            />
        </section>
    );
};

export default AnalysisSection;
